/**
 * CartManager
 * Direct conversion from TypeScript master CartManager.ts
 */
// @ts-nocheck
import { SideCartManager } from './side-cart-manager.js';
import { NotificationManager } from './notification-manager.js';
import { CartUpdateEvent } from '@theme/events';
class CartManager {
  static instance = null;
  path = '/cart';

  constructor() {
    this.notificationManager = NotificationManager.getInstance();
    this.state = this.parseCustomData(window.Pact?.cart);
    window.CartManager = CartManager;
    window.releaseInitialCartData?.();
  }

  static getInstance() {
    if (!CartManager.instance) {
      CartManager.instance = new CartManager();
    }
    return CartManager.instance;
  }

  _state = null;

  get state() {
    return this._state;
  }

  set state(state) {
    if (!state) return;

    const newState = this._state ? { ...this._state, ...state } : state;
    this._state = newState;
    if (this.stateChangeSubject$) {
      this.stateChangeSubject$.next(newState);
    }
  }

  // Observables (RxJS - simplified for JS)
  stateChangeSubject$ = null;
  stateChange$ = null;

  setState(state) {
    this.state = state;
  }

  getState() {
    return this.state;
  }

  parseCustomData(data) {
    if (!data) return null;

    const {
      line_item_variants,
      product_metafields,
      cart,
      custom_metafields,
      variant_metafields,
      seo_metafields,
      item_badges,
      discount_eligible_items
    } = data;

    cart.items.forEach((item, index) => {
      item.variant = line_item_variants[item.key];
      item.metafields = {
        ...product_metafields[index],
        custom: custom_metafields[index],
        variant: variant_metafields[index],
        seo: seo_metafields[index]
      };
      item.badges = item_badges[item.key];
      item.is_eligible_for_discount = discount_eligible_items ? discount_eligible_items[item.key] : false;
      item.free_shipping = data.free_shipping_items?.[index] || false;
      item.media_gallery_image = data.variant_media_gallery[item.id];

      // Skips Cloverly
      if (item.product_id == 7704109711498) {
        cart.item_count--;
      }

      if (item.properties?.['_rugBundleId'] && Object.keys(item.properties).length === 1) {
        item.hidden = true;
      }
    });

    return cart;
  }

  trackOpen() {
    const items = this.state?.items || [];
    const gtag = window.gtag || console.warn.bind(null, '[CartManager] gtag not found');
    const totalPrice = items.reduce((partialSum, item) => partialSum + item.price, 0);
    gtag('event', 'View Cart', {
      send_to: 'UA-224090997-1',
      currency: window.Shopify?.currency?.active || 'USD',
      value: totalPrice / 100,
      items: items.map(item => {
        return {
          item_id: item.sku,
          item_name: item.product_title,
          currency: window.Shopify?.currency?.active || 'USD',
          quantity: item.quantity,
          price: item.price / 100,
          item_variant: item.variant_title
        };
      })
    });
  }

  async get() {
    const path = `${this.path}?view=variant`;
    const cartData = await fetch(path);
    if (cartData.status === 200) {
      const json = await cartData.json();
      const cart = this.parseCustomData(json);
      this.state = cart;
      return cart;
    } else {
      throw new Error(`Something went wrong when fetching the cart. Shopify returned ${cartData.status} ${cartData.statusText}`);
    }
  }

  async change(key, quantity, properties) {
    const item = { quantity };
    item.id = key;
    if (properties) {
      item.properties = properties;
    }
    return this.changeMany([item]);
  }

  async changeMany(items) {
    console.log('[CartManager.changeMany] Items to change:', items);
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      console.log('[CartManager.changeMany] Changing item:', item);

      // Validate that item has id
      if (!item.id) {
        console.error('[CartManager.changeMany] Item missing id:', item);
        throw new Error('Item missing id parameter');
      }

      await this.call('change', item);
    }

    return this.get();
  }

  async add(id, quantity = 1, properties = {}, returnAdded = false) {
    const item = {
      id,
      quantity: quantity || 1,
      properties: properties || {}
    };
    return this.addMany([item], false);
  }

  async addMany(items, returnAdded = false) {
    const results = await this.call('add', { items });
    const gtag = window.gtag || console.log;
    const allItems = results && results.items && results.items.length ? results.items : [];
    const totalPrice = allItems.reduce((partialSum, item) => partialSum + item.price, 0);

    const SideCartManagerInstance = SideCartManager.getInstance();
    const eventName = SideCartManagerInstance.getStatus() === 'opened' ? 'Add Upsell' : 'Add To Cart';

    gtag('event', eventName, {
      currency: window.Shopify?.currency?.active || 'USD',
      value: totalPrice / 100,
      items: allItems.map(item => {
        return {
          item_id: item.sku,
          item_name: item.product_title,
          currency: window.Shopify?.currency?.active || 'USD',
          quantity: item.quantity,
          price: item.price / 100,
          item_variant: item.variant_title
        };
      })
    });
    return this.get();
  }

  async update(data) {
    await this.call('update', data);
    return this.get();
  }

  async call(controller, body = null) {
    let path = this.path;
    if (controller !== 'cart') path += `/${controller}.js`;

    const fetchOptions = {
      method: body === null ? 'GET' : 'POST',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      redirect: 'follow',
      body: body ? JSON.stringify(body) : null
    };

    const data = await fetch(path, fetchOptions);
    let cartJson;
    try {
      cartJson = await data.json();
    } catch (e) {
      if (window.NotificationManager) {
        window.NotificationManager.getInstance().notify({ type: 'error', title: 'Error', message: data.statusText });
      }
      throw new Error(`Something went wrong when fetching the cart. Shopify returned ${data.status} ${data.statusText}`);
    }
    if (data.status === 200) {
      const cart = await this.get();
      document.dispatchEvent(new CartUpdateEvent({ cart: cart }));
      // console.log(cart);
      const cartUpsellContainer = document.querySelector("[upsell-carousel]#cstm_cart_carousel_upsell");
      if (cartUpsellContainer) {
        if (cart.item_count === 0) {
          cartUpsellContainer.classList.add("hide");
        } else {
          cartUpsellContainer.classList.remove("hide");
        }
      }
      return cartJson;
    } else if (cartJson && cartJson.description) {
      if (window.NotificationManager) {
        window.NotificationManager.getInstance().notify({ type: 'error', title: 'Error', message: cartJson.description });
      }
      // Partial success: items were added despite 422 (e.g. "Only 3 items were added due to availability")
      const isPartialAdd = controller === 'add' && data.status === 422 &&
        (cartJson.description || '').toLowerCase().includes('added');
      if (isPartialAdd) {
        const cart = await this.get();
        document.dispatchEvent(new CartUpdateEvent({ cart: cart }));
        const cartUpsellContainer = document.querySelector("[upsell-carousel]#cstm_cart_carousel_upsell");
        if (cartUpsellContainer) {
          if (cart.item_count === 0) {
            cartUpsellContainer.classList.add("hide");
          } else {
            cartUpsellContainer.classList.remove("hide");
          }
        }
        return { items: cart.items || [] };
      }
      throw new Error(cartJson.description);
    } else {
      if (window.NotificationManager) {
        window.NotificationManager.getInstance().notify({ type: 'error', title: 'Error', message: data.statusText });
      }
      throw new Error(`Something went wrong when fetching the cart. Shopify returned ${data.status} ${data.statusText}`);
    }
  }
}

export default CartManager;
